import type { ConversationPriority } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Icon } from "@sagot/ui";
import type { SlaTarget } from "./inbox-model";
import { formatMinutes, targetState } from "./sla";

/** High and urgent conversations carry a chip (icon + word); normal ones stay quiet. */
export function PriorityChip({ priority }: { priority: ConversationPriority | undefined }) {
  const { t } = useTranslation();
  if (!priority || priority === "normal") return null;
  const urgent = priority === "urgent";
  return (
    <Badge tone={urgent ? "danger" : "warning"} data-testid="priority-chip" data-priority={priority}>
      <Icon name={urgent ? "alert-triangle" : "arrow-up"} className="size-3" />
      {t(`engage.inbox.priority.${priority}`)}
    </Badge>
  );
}

/** The SLA clock as words: "Due in 12m" while it runs, "Breached" once the target is missed (never colour alone). */
export function SlaChip({ target, now }: { target: SlaTarget | null | undefined; now: number }) {
  const { t } = useTranslation();
  const state = target ? targetState(target, now) : null;
  if (!target || !state) return null;
  const stage = t(`engage.inbox.sla.stages.${target.stage}`);
  if (state.kind === "breached") {
    return (
      <Badge tone="danger" title={stage} data-testid="sla-chip" data-sla="breached">
        <Icon name="alert-triangle" className="size-3" />
        {t("engage.inbox.sla.breached")}
      </Badge>
    );
  }
  return (
    <Badge tone={state.soon ? "warning" : "neutral"} title={stage} data-testid="sla-chip" data-sla="due">
      <Icon name="clock" className="size-3" />
      {t("engage.inbox.sla.dueIn", { time: formatMinutes(state.minutes) })}
    </Badge>
  );
}
